// src/pages/sqf/SqfDashboardPage.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import sqfService from '../../services/sqfService';
import { useLanguage, t } from '../../context/LanguageContext';
import SqfNav from '../../components/SqfNav';

const STATUS_CONFIG = {
  pending:     { label: 'Pending',     color: 'secondary' },
  in_progress: { label: 'In Progress', color: 'warning'   },
  submitted:   { label: 'Submitted',   color: 'info'      },
  qa_approved: { label: 'Approved',    color: 'success'   },
  qa_rejected: { label: 'Rejected',    color: 'danger'    },
  overdue:     { label: 'Overdue',     color: 'danger'    },
};

function statusBadge(status, lang) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'secondary' };
  const labelMap = {
    'Pending':     t('Pending',     'Pendiente',   lang),
    'In Progress': t('In Progress', 'En progreso', lang),
    'Submitted':   t('Submitted',   'Enviado',     lang),
    'Approved':    t('Approved',    'Aprobado',    lang),
    'Rejected':    t('Rejected',    'Rechazado',   lang),
    'Overdue':     t('Overdue',     'Vencido',     lang),
  };
  const displayLabel = labelMap[cfg.label] || cfg.label;
  return <span className={`badge bg-${cfg.color}`}>{displayLabel}</span>;
}

function instanceCardColor(status, dueAt) {
  if (status === 'qa_approved') return 'success';
  if (status === 'submitted')   return 'info';
  if (status === 'overdue')     return 'danger';
  if (status === 'in_progress') return 'warning';
  if (status === 'pending' && dueAt) {
    const diff = new Date(dueAt) - new Date();
    if (diff < 0) return 'danger';
    if (diff < 30 * 60 * 1000) return 'warning'; // due within 30 min
  }
  return 'secondary';
}

export default function SqfDashboardPage() {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    sqfService.getDashboard()
      .then(d => {
        // Deduplicate today's instances by id — guards against stale race-condition duplicates
        const seen = new Set();
        d.today.instances = d.today.instances.filter(i => {
          if (seen.has(i.id)) return false;
          seen.add(i.id);
          return true;
        });
        // Recompute statusCounts from the deduplicated list so counts stay in sync
        d.today.statusCounts = d.today.instances.reduce((acc, i) => {
          acc[i.status] = (acc[i.status] || 0) + 1;
          return acc;
        }, {});
        setData(d);
      })
      .catch(() => setError(t('Failed to load dashboard.', 'Error al cargar el panel.', lang)))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="container mt-4 text-center text-muted py-5">
      <div className="spinner-border spinner-border-sm me-2" role="status" />
      {t('Loading dashboard…', 'Cargando panel…', lang)}
    </div>
  );

  if (error) return (
    <div className="container mt-4">
      <div className="alert alert-danger">{error}</div>
    </div>
  );

  const { today, openCorrectiveActions, pendingQaLogs, expiringContractors } = data;
  const counts = today.statusCounts || {};
  const total = today.instances.length;
  const done = (counts.qa_approved || 0) + (counts.submitted || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <>
      <SqfNav />
      <div className="container mt-4 pb-5">
      <h4 className="mb-3">{t('SQF Daily Checklists', 'Listas de verificación SQF diarias', lang)}</h4>

      {/* Summary cards */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3">
          <div className="card text-center border-0 shadow-sm h-100">
            <div className="card-body py-3">
              <div style={{ fontSize: 28, fontWeight: 700, color: pct === 100 ? '#198754' : pct >= 50 ? '#fd7e14' : '#dc3545' }}>{pct}%</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{t("Today's Completion", 'Completado hoy', lang)}</div>
              <div className="text-muted" style={{ fontSize: 11 }}>{done} / {total} {t('done', 'completado', lang)}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className={`card text-center border-0 shadow-sm h-100 ${counts.overdue > 0 ? 'border-danger' : ''}`}>
            <div className="card-body py-3">
              <div style={{ fontSize: 28, fontWeight: 700, color: counts.overdue > 0 ? '#dc3545' : '#6c757d' }}>{counts.overdue || 0}</div>
              <div className="text-muted" style={{ fontSize: 12 }}>{t('Overdue', 'Vencido', lang)}</div>
            </div>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <Link to="/sqf/corrective-actions" className="text-decoration-none">
            <div className={`card text-center border-0 shadow-sm h-100 ${openCorrectiveActions > 0 ? 'border-warning' : ''}`}>
              <div className="card-body py-3">
                <div style={{ fontSize: 28, fontWeight: 700, color: openCorrectiveActions > 0 ? '#fd7e14' : '#6c757d' }}>{openCorrectiveActions}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>{t('Open Corrective Actions', 'Acciones correctivas abiertas', lang)}</div>
              </div>
            </div>
          </Link>
        </div>
        <div className="col-6 col-md-3">
          <Link to="/sqf/checklists?tab=pending-qa" className="text-decoration-none">
            <div className={`card text-center border-0 shadow-sm h-100 ${pendingQaLogs > 0 ? 'border-info' : ''}`}>
              <div className="card-body py-3">
                <div style={{ fontSize: 28, fontWeight: 700, color: pendingQaLogs > 0 ? '#0dcaf0' : '#6c757d' }}>{pendingQaLogs}</div>
                <div className="text-muted" style={{ fontSize: 12 }}>{t('Pending QA Review', 'Revisión QA pendiente', lang)}</div>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Expiring contractor agreements */}
      {expiringContractors.length > 0 && (
        <div className="alert alert-warning py-2 mb-4">
          <strong>{t('Contractor agreements require attention:', 'Los acuerdos de contratistas requieren atención:', lang)}</strong>{' '}
          {expiringContractors.map((c, i) => (
            <span key={c.id}>
              {i > 0 && ', '}
              <Link to="/sqf/contractors" className="alert-link">{c.name}</Link>
              {' '}({t('expires', 'vence', lang)} {new Date(c.agreementExpiresAt).toLocaleDateString()})
            </span>
          ))}
          <Link to="/sqf/contractors" className="ms-2 alert-link">{t('Manage →', 'Gestionar →', lang)}</Link>
        </div>
      )}

      {/* Today's checklists */}
      <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>
        {t('Today', 'Hoy', lang)} — {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
      </h6>

      {today.instances.length === 0 ? (
        <div className="text-center text-muted py-5">
          <div style={{ fontSize: 36 }}>✅</div>
          <p className="mt-2">{t('No checklists scheduled for today.', 'No hay listas programadas para hoy.', lang)}</p>
          <Link to="/sqf/checklists" className="btn btn-sm btn-outline-primary">{t('Create On-Demand Checklist', 'Crear lista bajo demanda', lang)}</Link>
        </div>
      ) : (
        <div className="row g-2">
          {today.instances.map(inst => {
            const color = instanceCardColor(inst.status, inst.dueAt);
            const template = inst.Template;
            const log = inst.Log;
            return (
              <div key={inst.id} className="col-12 col-md-6 col-lg-4">
                <div className={`card border-${color} border-start border-start-4 shadow-sm h-100`} style={{ cursor: 'pointer' }}
                  onClick={() => inst.Log?.id && (inst.status === 'qa_approved' || inst.status === 'submitted')
                    ? navigate(`/sqf/logs/${inst.Log.id}`)
                    : navigate(`/sqf/fill/${inst.id}`)
                  }>
                  <div className="card-body py-2 px-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="fw-semibold" style={{ fontSize: 14 }}>{t(template?.name, template?.nameEs, lang)}</div>
                      {statusBadge(inst.status, lang)}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {inst.shift !== 'na' && <span className="me-2 text-capitalize">{inst.shift} {t('shift', 'turno', lang)}</span>}
                      {inst.dueAt && <span>{t('Due', 'Vence', lang)} {new Date(inst.dueAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                    </div>
                    {log?.hasDeviation && (
                      <span className="badge bg-warning text-dark mt-1" style={{ fontSize: 10 }}>⚠ {t('Deviation', 'Desviación', lang)}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
    </>
  );
}
